import { useState } from 'react'
import { Keyboard, Text, View } from 'react-native'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useController, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { semantic } from '@/constants/colors'
import { AppealKind } from '@/constants/common'
import { BUY_ORDER_REQUEST_APPEAL } from '@/constants/graphql/mutations'
import { GET_ORDER_APPEAL_UPLOADS_PRESIGNED } from '@/constants/graphql/queries'
import { AddImageInterface, BuyOrder, ImageAsset } from '@/types/common'
import { preparePickedImageUpload } from '@/utils/imageUpload'
import { launchMediaLibraryAsync } from '@/utils/mediaPicker'
import { generateRandomFileName, uploadImageToPresigned } from '@/utils/url'
import { Button, FormScrollView, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import { Textarea } from '@/components/form-control'
import { UploadImage } from '@/components/icon'
import { ImageButton } from '@/components/ImageButton'
import { useImageViewer } from '@/components/ImageViewer'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import { ConfirmModal } from '@/components/orders/appeal/ConfirmModal'
import UploadVideoButton from '@/components/UploadVideoButton'

type formValues = {
  reason: string
  videoUri: string
}

const defaultReason: formValues = {
  reason: '',
  videoUri: ''
}
export function AppealDetailsForm({
  data: orderData,
  appealKind,
  completeAppeal
}: {
  data: BuyOrder
  appealKind: AppealKind
  completeAppeal: () => void
}) {
  const [videoSource, setVideoSource] = useState('')
  const loading = useLoadingIndicator()
  const { t } = useTranslation()
  const { bottom } = useSafeAreaInsets()
  const [images, setImages] = useState<ImageAsset[]>([])

  const { openModal, closeModal } = useModal()
  const { control, handleSubmit, watch, setError } = useForm<formValues>({ defaultValues: defaultReason })
  const {
    field: { onChange: onChangeVideoUri }
  } = useController({ control, name: 'videoUri' })
  const { setSource, onDeleteCallback } = useImageViewer()
  const toast = useToast()

  const reason = watch('reason')

  const [submitAppeal] = useMutation(BUY_ORDER_REQUEST_APPEAL, {
    onCompleted: () => {
      completeAppeal()
    },
    onError: (error) => {
      loading.stop()
      toast.show({ variant: 'error', description: t('orderAppeal:details.submitFailed') })
      console.error('buyerUploadPaymentProof error:', error)
    }
  })

  const [getUploadsPresigned] = useLazyQuery(GET_ORDER_APPEAL_UPLOADS_PRESIGNED, {
    onError: (error) => {
      console.error(error)
      loading.stop()
    }
  })

  const addImage: AddImageInterface = (
    uri: string,
    _index: number,
    fileName?: string | null,
    mimeType?: string | null
  ) => {
    const extension = typeof fileName === 'string' ? fileName.split('.').at(-1) : 'jpg'
    const name = `${generateRandomFileName()}.${extension}`
    const asset = {
      uri,
      fileName: name,
      mimeType
    }

    const nextImages = [...images]
    if (!nextImages[0]) {
      nextImages[0] = asset
    } else if (!nextImages[1]) {
      nextImages[1] = asset
    } else {
      nextImages[2] = asset
    }
    setImages(nextImages)
  }

  const deleteImage = (deleteIndex: number) => {
    setImages([...images.filter((_, index) => index !== deleteIndex)])
  }

  async function pickImage() {
    const result = await launchMediaLibraryAsync({
      mediaTypes: ['images'],
      quality: 1,
      allowsMultipleSelection: false
    })

    if (!result.canceled) {
      const prepared = await preparePickedImageUpload(result.assets[0])
      if (!prepared.ok) {
        toast.show({
          variant: 'error',
          description: prepared.reason === 'type' ? t('orders:imageFormatInvalid') : t('orders:imageTooLarge')
        })
        return
      }
      addImage(prepared.asset.uri, 0, prepared.asset.fileName, prepared.asset.mimeType)
    }
  }

  const handleViewAddImage = (index: number) => {
    if (images[index]) {
      setSource(images[index].uri)
      onDeleteCallback.current = () => deleteImage(index)
    } else {
      pickImage()
    }
  }

  const startSubmission = async (formData: formValues) => {
    closeModal()
    loading.start()

    const firstFile = images[0]

    if (!firstFile) {
      // Directly submit if no images
      submitAppeal({
        variables: {
          buyOrderId: orderData.id,
          appealKind,
          appealProofFileName: [],
          appealReason: formData.reason
        }
      })
      return
    }

    let fileNames
    try {
      const responses = await Promise.all(
        images.map(({ fileName }) => {
          return getUploadsPresigned({
            variables: {
              buyOrderId: orderData.id,
              fileName,
              appealKind
            }
          })
        })
      )
      // Find images
      fileNames = responses.map((response) => {
        const uploadUrl = response.data.getOrderAppealUploadsPresigned
        const urlObject = new URL(uploadUrl)
        const paths = urlObject.pathname.split('/')
        return decodeURIComponent(paths[paths.length - 1])
      })
      // Upload images
      await Promise.all(
        fileNames.map((fileName, index) => {
          return uploadImageToPresigned(images[index], fileName, responses[index].data.getOrderAppealUploadsPresigned)
        })
      )
    } catch (e) {
      loading.stop()
      console.log(e)
      console.log('fail to upload images!')
      toast.show({ variant: 'error', description: t('auth:toast.error.timeoutError') })
      return
    }

    submitAppeal({
      variables: {
        buyOrderId: orderData.id,
        appealKind,
        appealProofFileName: images.map((image) => image.fileName),
        appealReason: formData.reason
      }
    })
  }

  const startSubmissionForPaymentNotReceived = async (formData: formValues) => {
    closeModal()
    loading.start()
    // console.log({ buyOrderId: orderData.id, sellOrderId: parentId, ...formData })
    let extension = 'mp4'
    const mimeType = formData.videoUri.split(';')[0].split(':')[1]
    switch (mimeType) {
      case 'video/x-msvideo': {
        extension = 'avi'
        break
      }
      case 'video/mp4': {
        extension = 'mp4'
        break
      }
      case 'video/quicktime': {
        extension = 'mov'
        break
      }
    }
    const inputFileName = `${generateRandomFileName()}.${extension}`
    // console.log({ inputFileName, appealKind })
    const response: Record<string, any> = await getUploadsPresigned({
      variables: {
        buyOrderId: orderData.id,
        fileName: inputFileName,
        appealKind
      }
    })

    const uploadUrl = response.data.getOrderAppealUploadsPresigned

    // Find the image
    let fileName
    try {
      const urlObject = new URL(uploadUrl)
      const paths = urlObject.pathname.split('/')
      fileName = paths[paths.length - 1]
    } catch (error) {
      // TODO: Shouldn't come here, but just in case, should set some status to error
      console.error('Error parsing URL:', error)
      loading.stop()
      return null
    }

    // Upload
    try {
      await uploadImageToPresigned({ uri: formData.videoUri }, fileName, uploadUrl)
    } catch (e) {
      console.error('Error uploading image: ', e)
      loading.stop()
      throw e
    }

    // Call backend
    submitAppeal({
      variables: {
        buyOrderId: orderData.id,
        appealKind,
        appealProofFileName: [inputFileName],
        appealReason: formData.reason
      }
    })
  }

  function openConfirmModal() {
    Keyboard.dismiss()
    if (reason.length > 800) {
      setError('reason', { message: t('orderAppeal:details.maxCharacters') })
    } else {
      openModal({
        children: (
          <ConfirmModal
            onSubmit={handleSubmit(
              appealKind !== AppealKind.SellerNotReceivedPayment
                ? startSubmission
                : startSubmissionForPaymentNotReceived
            )}
          />
        ),
        onClose: closeModal
      })
    }
  }

  return (
    <>
      <FormScrollView className={'flex-1'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
        <View className={'border-b border-border-10 bg-background-primary p-4'}>
          <View className={'flex-row justify-between'}>
            <Text className={'text-sm text-text-secondary'}>{t('orderAppeal:selectKind.title')}</Text>
            <Text className={'text-sm font-medium text-text-primary'}>
              {t(`orderAppeal:selectKind.options.${AppealKind[appealKind]}`)}
            </Text>
          </View>
          <View className={'h-6'} />
          {appealKind !== AppealKind.SellerNotReceivedPayment ? (
            <>
              <Textarea
                label={t('orderAppeal:details.title')}
                control={control}
                name={'reason'}
                placeholder={t('orderAppeal:details.placeholder')}
                returnKeyType={'done'}
              />
              <View className={'h-2'} />
              <View className={'gap-1 bg-background-primary'}>
                <Text className={'text-sm font-medium text-text-secondary'}>
                  {t('orderAppeal:details.uploadImage')}
                </Text>
                <View className={'flex-row gap-2'}>
                  {[0, 1, 2].map((index) => (
                    <ImageButton
                      key={index}
                      image={images[index]?.uri}
                      onPress={() => {
                        Keyboard.dismiss()
                        handleViewAddImage(index)
                      }}
                      blankElement={<UploadImage width={24} height={24} color={semantic.text.quaternary} />}
                    />
                  ))}
                </View>
                <Text className={'text-sm text-text-secondary'}>{t('orderAppeal:details.uploadImageTip')}</Text>
              </View>
            </>
          ) : (
            <>
              <View>
                <Text className={'text-sm text-text-secondary'}>
                  {t('orderAppeal:SellerNotReceivedPayment.description')}
                </Text>
              </View>
              <View className={'h-6'} />
              <UploadVideoButton
                videoSource={videoSource}
                setVideoSource={setVideoSource}
                onChangeVideoUri={onChangeVideoUri}
              />
            </>
          )}
        </View>
      </FormScrollView>
      <BottomActionPanel>
        <Button
          size={'md'}
          intent={'primary'}
          variant={'solid'}
          className={'flex-1'}
          onPress={openConfirmModal}
          disabled={
            loading.isLoading ||
            (appealKind === AppealKind.SellerNotReceivedPayment && (!videoSource || videoSource === 'loading'))
          }
        >
          {t(`orderAppeal:submitButton.details`)}
        </Button>
      </BottomActionPanel>
    </>
  )
}
